<template>
  <RevCard class="p-24">
    <div class="flex flex-wrap gap-16">
      <RevTag
        v-for="category in categories"
        :key="category.id"
        :label="category.name"
        variant="secondary"
      />
    </div>
  </RevCard>
  <RevCard v-if="freeMethods.length" class="space-y-24 p-24">
    <div class="flex">
      <IconTruck class="mr-8" />
      <div>
        <span class="body-1-bold">
          {{ i18n(translations.freeShippingTitle) }}
        </span>
        <p class="body-2">
          {{ i18n(translations.freeShippingDescription) }}
        </p>
      </div>
    </div>
    <MethodList :category-group-metadata is-free :methods="freeMethods" />
  </RevCard>
  <RevCard v-if="values.paidMethods.length" class="space-y-24 p-24">
    <div class="flex">
      <IconTruck class="mr-8" />
      <div>
        <span class="body-1-bold">
          {{ i18n(translations.paidShippingTitle) }}
        </span>
        <p class="body-2">
          {{ i18n(translations.paidShippingDescription) }}
        </p>
      </div>
    </div>
    <MethodList
      :category-group-metadata
      :is-free="false"
      :methods="values.paidMethods"
    />
  </RevCard>
  <RevCard v-if="collectionPointMethods.length" class="space-y-24 p-24">
    <div class="flex">
      <IconShop class="mr-8" />
      <div>
        <span class="body-1-bold">
          {{ i18n(translations.collectionPointTitle) }}
        </span>
        <p class="body-2">
          {{ i18n(translations.collectionPointDescription) }}
        </p>
      </div>
    </div>
    <MethodList
      :category-group-metadata
      is-free
      :methods="collectionPointMethods"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { ShippingCategoryGroup } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevTag } from '@ds/components/Tag'
import { IconShop } from '@ds/icons/IconShop'
import { IconTruck } from '@ds/icons/IconTruck'

import type { ShippingCategoryGroupMetadata } from '../../../../CategoryList.types'
import { getCarrier } from '../../../../CategoryList.utils'
import MethodList from '../../../MethodList/MethodList.vue'
import type { FormValues } from '../../RuleDrawer.types'

import translations from './RuleSummary.translations'

const props = defineProps<{
  values: FormValues
  categoryGroup: ShippingCategoryGroup
  categoryGroupMetadata: ShippingCategoryGroupMetadata
}>()

const i18n = useI18n()

const freeMethods = computed(() =>
  props.values.freeMethods.filter(({ shippingProvider }) => {
    const carrier = getCarrier(
      props.categoryGroupMetadata.shippingMethods.providerOptions,
      shippingProvider,
    )

    return !carrier?.collectionPoint
  }),
)

const collectionPointMethods = computed(() =>
  props.values.freeMethods.filter(({ shippingProvider }) => {
    const carrier = getCarrier(
      props.categoryGroupMetadata.shippingMethods.providerOptions,
      shippingProvider,
    )

    return carrier?.collectionPoint
  }),
)

const categories = computed(() =>
  props.categoryGroup.shippingCategories.filter(({ id }) =>
    props.values.categories.includes(id),
  ),
)
</script>
